import React, { useState, useEffect } from "react";
import SpaContactModal from "../../SpaContactModal";
import { Clock, Star, Leaf, Heart } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { Button } from "@/components/ui/button";

interface TherapeuticTreatmentsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const TherapeuticTreatmentsModal: React.FC<TherapeuticTreatmentsModalProps> = ({
  isOpen,
  onClose,
}) => {
  const [isSpaModalOpen, setIsSpaModalOpen] = useState(false);
  const { t } = useLanguage();

  // Track modal open
  useEffect(() => {
    if (isOpen && typeof window !== "undefined") {
      window.dataLayer = window.dataLayer || [];

      // Clear all tracking variables first (prevents data pollution)
      // NOTE: button_text, button_location, and modal_name are preserved to retain context
      window.dataLayer.push({
        // Clear modal variables (will be reset below)
        modal_type: undefined,
        modal_name: undefined,
        parent_modal: undefined,
        page_location: undefined,
        // Preserve button variables (retain context of triggering action)
        // button_text: undefined,     // ← Keep this!
        // button_location: undefined, // ← Keep this!
        // Clear booking variables
        room_type: undefined,
        // Clear contact action variables
        contact_type: undefined,
        contact_source: undefined,
        event_category: undefined,
        event_label: undefined,
        phone_number: undefined,
        email_address: undefined,
        whatsapp_number: undefined,
        region: undefined,
        destination: undefined,
        booking_method: undefined,
        // Clear event consultation variables
        event_type: undefined,
        consultation_method: undefined,
        service_category: undefined,
        // Clear Calendly variables
        calendly_action: undefined,
        calendly_location: undefined,
      });

      // Now push the actual modal open event with clean data
      window.dataLayer.push({
        event: "modal_open",
        modal_type: "spa_treatment",
        modal_name: "therapeutic_treatments",
        page_location: window.location.pathname,
      });
    }
  }, [isOpen]);

  return (
    <div
      className={`fixed inset-0 z-[100] flex items-center justify-center ${isOpen ? "block" : "hidden"}`}
      data-oid="6ak1f1k"
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
        data-oid="_jkd38n"
      />

      {/* Modal Content */}
      <div
        className="relative bg-elegant-white rounded-xl shadow-2xl max-w-5xl w-full mx-4 max-h-[90vh] overflow-y-auto"
        data-oid="7yvdvj2"
      >
        {/* Header */}
        <div
          className="sticky top-0 bg-elegant-white border-b border-gray-200 px-6 py-4 rounded-t-xl"
          data-oid="9istha4"
        >
          <div className="flex items-center justify-between" data-oid="x5szta4">
            <h2
              className="text-2xl md:text-3xl font-playfair text-gray-900"
              data-oid="0-axf_v"
            >
              {t("spa.therapeuticTreatments.title")}
            </h2>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              aria-label="Close modal"
              data-oid="q:3ernv"
            >
              <svg
                className="w-6 h-6 text-gray-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                data-oid="ehuk0gs"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                  data-oid="f3:9dwp"
                />
              </svg>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6" data-oid="0i6eklp">
          <div className="mb-6" data-oid=":w._.g_">
            <p className="text-lg text-gray-900 mb-6" data-oid=".d790eg">
              {t("spa.therapeuticTreatments.description")}
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-8 mb-8" data-oid="b-v00fw">
            {/* Relaxation & Rejuvenation Treatments */}
            <div
              className="bg-pebble-beige rounded-lg p-6 shadow-lg"
              data-oid="ejy8hd."
            >
              <div className="flex items-center mb-6" data-oid="p81:-n_">
                <Heart
                  className="w-8 h-8 text-alpine-gold mr-3"
                  data-oid="ajwuq01"
                />

                <h3
                  className="text-2xl font-playfair font-semibold text-gray-900"
                  data-oid="s832vql"
                >
                  {t("spa.therapeuticTreatments.relaxationRejuvenation.title")}
                </h3>
              </div>

              <div className="space-y-6" data-oid="9rpmj1g">
                <div
                  className="border-b border-gray-200 pb-4"
                  data-oid="gdb9keu"
                >
                  <div
                    className="flex justify-between items-start mb-2"
                    data-oid="1iw3si-"
                  >
                    <h4
                      className="font-semibold text-gray-800"
                      data-oid="05mp.:r"
                    >
                      {t(
                        "spa.therapeuticTreatments.relaxationRejuvenation.treatments.alpineMountainMassage.title",
                      )}
                    </h4>
                    <span
                      className="text-alpine-gold font-semibold"
                      data-oid="9z4-:y3"
                    >
                      {t(
                        "spa.therapeuticTreatments.relaxationRejuvenation.treatments.alpineMountainMassage.price",
                      )}
                    </span>
                  </div>
                  <div className="flex items-center mb-2" data-oid="a_9qvpf">
                    <Clock
                      className="w-4 h-4 text-gray-500 mr-1"
                      data-oid=":ade2e4"
                    />

                    <span className="text-sm text-gray-600" data-oid="c18ss-s">
                      {t(
                        "spa.therapeuticTreatments.relaxationRejuvenation.treatments.alpineMountainMassage.duration",
                      )}
                    </span>
                  </div>
                  <p className="text-gray-900 text-sm" data-oid="lucann4">
                    {t(
                      "spa.therapeuticTreatments.relaxationRejuvenation.treatments.alpineMountainMassage.description",
                    )}
                  </p>
                </div>

                <div
                  className="border-b border-gray-200 pb-4"
                  data-oid="x:l4g24"
                >
                  <div
                    className="flex justify-between items-start mb-2"
                    data-oid="egwiom8"
                  >
                    <h4
                      className="font-semibold text-gray-800"
                      data-oid="7aec:c8"
                    >
                      {t(
                        "spa.therapeuticTreatments.relaxationRejuvenation.treatments.lakeThunAromatherapy.title",
                      )}
                    </h4>
                    <span
                      className="text-alpine-gold font-semibold"
                      data-oid="kbqivgh"
                    >
                      {t(
                        "spa.therapeuticTreatments.relaxationRejuvenation.treatments.lakeThunAromatherapy.price",
                      )}
                    </span>
                  </div>
                  <div className="flex items-center mb-2" data-oid="our8g35">
                    <Clock
                      className="w-4 h-4 text-gray-500 mr-1"
                      data-oid="g73uyb0"
                    />

                    <span className="text-sm text-gray-600" data-oid="kd4398s">
                      {t(
                        "spa.therapeuticTreatments.relaxationRejuvenation.treatments.lakeThunAromatherapy.duration",
                      )}
                    </span>
                  </div>
                  <p className="text-gray-900 text-sm" data-oid="qnq_57o">
                    {t(
                      "spa.therapeuticTreatments.relaxationRejuvenation.treatments.lakeThunAromatherapy.description",
                    )}
                  </p>
                </div>

                <div
                  className="border-b border-gray-200 pb-4"
                  data-oid="nkzo9dr"
                >
                  <div
                    className="flex justify-between items-start mb-2"
                    data-oid="vfme-pu"
                  >
                    <h4
                      className="font-semibold text-gray-800"
                      data-oid="mn33o6z"
                    >
                      {t(
                        "spa.therapeuticTreatments.relaxationRejuvenation.treatments.swissStoneTherapy.title",
                      )}
                    </h4>
                    <span
                      className="text-alpine-gold font-semibold"
                      data-oid="z3muk8a"
                    >
                      {t(
                        "spa.therapeuticTreatments.relaxationRejuvenation.treatments.swissStoneTherapy.price",
                      )}
                    </span>
                  </div>
                  <div className="flex items-center mb-2" data-oid="b50:6sk">
                    <Clock
                      className="w-4 h-4 text-gray-500 mr-1"
                      data-oid="w4hr7qs"
                    />

                    <span className="text-sm text-gray-600" data-oid="p4j8fn9">
                      {t(
                        "spa.therapeuticTreatments.relaxationRejuvenation.treatments.swissStoneTherapy.duration",
                      )}
                    </span>
                  </div>
                  <p className="text-gray-900 text-sm" data-oid="jcm95:s">
                    {t(
                      "spa.therapeuticTreatments.relaxationRejuvenation.treatments.swissStoneTherapy.description",
                    )}
                  </p>
                </div>

                <div data-oid="8ch7b5:">
                  <div
                    className="flex justify-between items-start mb-2"
                    data-oid="tqihnlc"
                  >
                    <h4
                      className="font-semibold text-gray-800"
                      data-oid="3ijedtm"
                    >
                      {t(
                        "spa.therapeuticTreatments.relaxationRejuvenation.treatments.alpineHerbalSteamBath.title",
                      )}
                    </h4>
                    <span
                      className="text-alpine-gold font-semibold"
                      data-oid="8hgurqy"
                    >
                      {t(
                        "spa.therapeuticTreatments.relaxationRejuvenation.treatments.alpineHerbalSteamBath.price",
                      )}
                    </span>
                  </div>
                  <div className="flex items-center mb-2" data-oid="xalwg.9">
                    <Clock
                      className="w-4 h-4 text-gray-500 mr-1"
                      data-oid="e:b673f"
                    />

                    <span className="text-sm text-gray-600" data-oid="1tsjoiq">
                      {t(
                        "spa.therapeuticTreatments.relaxationRejuvenation.treatments.alpineHerbalSteamBath.duration",
                      )}
                    </span>
                  </div>
                  <p className="text-gray-900 text-sm" data-oid="_s3gqm3">
                    {t(
                      "spa.therapeuticTreatments.relaxationRejuvenation.treatments.alpineHerbalSteamBath.description",
                    )}
                  </p>
                </div>
              </div>
            </div>

            {/* Advanced Wellness Treatments */}
            <div
              className="bg-pebble-beige rounded-lg p-6 shadow-lg"
              data-oid="a.sklaq"
            >
              <div className="flex items-center mb-6" data-oid="_mug3rx">
                <Star
                  className="w-8 h-8 text-alpine-gold mr-3"
                  data-oid="ntzb4rs"
                />

                <h3
                  className="text-2xl font-playfair font-semibold text-gray-900"
                  data-oid="ah7yqxx"
                >
                  {t("spa.therapeuticTreatments.advancedWellness.title")}
                </h3>
              </div>

              <div className="space-y-6" data-oid="c0r62f4">
                <div
                  className="border-b border-gray-200 pb-4"
                  data-oid="bslzzg5"
                >
                  <div
                    className="flex justify-between items-start mb-2"
                    data-oid="d:9zr3s"
                  >
                    <h4
                      className="font-semibold text-gray-800"
                      data-oid="8g7td6i"
                    >
                      {t(
                        "spa.therapeuticTreatments.advancedWellness.treatments.bioScan.title",
                      )}
                    </h4>
                    <span
                      className="text-alpine-gold font-semibold"
                      data-oid="w3gbk01"
                    >
                      {t(
                        "spa.therapeuticTreatments.advancedWellness.treatments.bioScan.price",
                      )}
                    </span>
                  </div>
                  <div className="flex items-center mb-2" data-oid="8fwwm0j">
                    <Clock
                      className="w-4 h-4 text-gray-500 mr-1"
                      data-oid="_9mn0yy"
                    />

                    <span className="text-sm text-gray-600" data-oid="j779unl">
                      {t(
                        "spa.therapeuticTreatments.advancedWellness.treatments.bioScan.duration",
                      )}
                    </span>
                  </div>
                  <p className="text-gray-900 text-sm" data-oid="gt:xxl6">
                    {t(
                      "spa.therapeuticTreatments.advancedWellness.treatments.bioScan.description",
                    )}
                  </p>
                </div>

                <div
                  className="border-b border-gray-200 pb-4"
                  data-oid="9x22chs"
                >
                  <div
                    className="flex justify-between items-start mb-2"
                    data-oid="lqo9vkz"
                  >
                    <h4
                      className="font-semibold text-gray-800"
                      data-oid="deluq.1"
                    >
                      {t(
                        "spa.therapeuticTreatments.advancedWellness.treatments.nutritionCoaching.title",
                      )}
                    </h4>
                    <span
                      className="text-alpine-gold font-semibold"
                      data-oid=":7_6vxi"
                    >
                      {t(
                        "spa.therapeuticTreatments.advancedWellness.treatments.nutritionCoaching.price",
                      )}
                    </span>
                  </div>
                  <div className="flex items-center mb-2" data-oid="n:rqalg">
                    <Clock
                      className="w-4 h-4 text-gray-500 mr-1"
                      data-oid="7:m_7s6"
                    />

                    <span className="text-sm text-gray-600" data-oid="4aeaz.3">
                      {t(
                        "spa.therapeuticTreatments.advancedWellness.treatments.nutritionCoaching.duration",
                      )}
                    </span>
                  </div>
                  <p className="text-gray-900 text-sm" data-oid="o.o6igh">
                    {t(
                      "spa.therapeuticTreatments.advancedWellness.treatments.nutritionCoaching.description",
                    )}
                  </p>
                </div>

                <div
                  className="border-b border-gray-200 pb-4"
                  data-oid="k5erk-x"
                >
                  <div
                    className="flex justify-between items-start mb-2"
                    data-oid="o4kkdla"
                  >
                    <h4
                      className="font-semibold text-gray-800"
                      data-oid="js_krl6"
                    >
                      {t(
                        "spa.therapeuticTreatments.advancedWellness.treatments.lightTherapy.title",
                      )}
                    </h4>
                    <span
                      className="text-alpine-gold font-semibold"
                      data-oid="pempz2a"
                    >
                      {t(
                        "spa.therapeuticTreatments.advancedWellness.treatments.lightTherapy.price",
                      )}
                    </span>
                  </div>
                  <div className="flex items-center mb-2" data-oid="31k1-2p">
                    <Clock
                      className="w-4 h-4 text-gray-500 mr-1"
                      data-oid="m9.cest"
                    />

                    <span className="text-sm text-gray-600" data-oid="p6t8gq1">
                      {t(
                        "spa.therapeuticTreatments.advancedWellness.treatments.lightTherapy.duration",
                      )}
                    </span>
                  </div>
                  <p className="text-gray-900 text-sm" data-oid="l48-jv0">
                    {t(
                      "spa.therapeuticTreatments.advancedWellness.treatments.lightTherapy.description",
                    )}
                  </p>
                </div>

                <div
                  className="border-b border-gray-200 pb-4"
                  data-oid="1q:zx2a"
                >
                  <div
                    className="flex justify-between items-start mb-2"
                    data-oid="80t.i-:"
                  >
                    <h4
                      className="font-semibold text-gray-800"
                      data-oid="rp1k0yo"
                    >
                      {t(
                        "spa.therapeuticTreatments.advancedWellness.treatments.pelviPowerTraining.title",
                      )}
                    </h4>
                    <span
                      className="text-alpine-gold font-semibold"
                      data-oid="sb_.tuw"
                    >
                      {t(
                        "spa.therapeuticTreatments.advancedWellness.treatments.pelviPowerTraining.price",
                      )}
                    </span>
                  </div>
                  <div className="flex items-center mb-2" data-oid="qirfj83">
                    <Clock
                      className="w-4 h-4 text-gray-500 mr-1"
                      data-oid="xmbrazy"
                    />

                    <span className="text-sm text-gray-600" data-oid="5rq9u-r">
                      {t(
                        "spa.therapeuticTreatments.advancedWellness.treatments.pelviPowerTraining.duration",
                      )}
                    </span>
                  </div>
                  <p className="text-gray-900 text-sm" data-oid="7qqn4ty">
                    {t(
                      "spa.therapeuticTreatments.advancedWellness.treatments.pelviPowerTraining.description",
                    )}
                  </p>
                </div>

                <div data-oid="-jjjg15">
                  <div
                    className="flex justify-between items-start mb-2"
                    data-oid="lqn.c2."
                  >
                    <h4
                      className="font-semibold text-gray-800"
                      data-oid="n:7zk-v"
                    >
                      {t(
                        "spa.therapeuticTreatments.advancedWellness.treatments.infraredSauna.title",
                      )}
                    </h4>
                    <span
                      className="text-alpine-gold font-semibold"
                      data-oid="icuqfas"
                    >
                      {t(
                        "spa.therapeuticTreatments.advancedWellness.treatments.infraredSauna.price",
                      )}
                    </span>
                  </div>
                  <p className="text-gray-900 text-sm" data-oid="jn7o4cc">
                    {t(
                      "spa.therapeuticTreatments.advancedWellness.treatments.infraredSauna.description",
                    )}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Concierge Booking */}
          <div
            className="bg-alpine-gold/10 border border-alpine-gold/20 rounded-lg p-6 text-center"
            data-oid="zoddbs6"
          >
            <h4
              className="text-lg font-playfair font-semibold text-gray-900 mb-2"
              data-oid="_rbesnh"
            >
              {t("spa.therapeuticTreatments.readyToBook")}
            </h4>
            <p className="text-gray-900 mb-4" data-oid="coqzlym">
              {t("spa.therapeuticTreatments.contactConciergeDesc")}
            </p>
            <Button
              onClick={() => setIsSpaModalOpen(true)}
              trackingType="contact_modal"
              trackingLabel="Contact for Spa Booking"
              trackingLocation="treatment_modal"
              trackingData={{
                modalType: "spa",
                parentModal: "therapeutic_treatments",
              }}
              className="bg-alpine-gold hover:bg-alpine-gold/90 text-white px-6 py-3 text-base font-semibold rounded-full shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
              data-oid="g60_z6e"
            >
              {t("spa.therapeuticTreatments.contactConciergeButton")}
            </Button>
          </div>
        </div>
      </div>

      {/* Spa Contact Modal */}
      <SpaContactModal
        isOpen={isSpaModalOpen}
        onClose={() => setIsSpaModalOpen(false)}
        parentModal="therapeutic_treatments"
        data-oid="49pk02j"
      />
    </div>
  );
};

export default TherapeuticTreatmentsModal;
